import axios from './axios';
import { createResourceApi } from './resource';

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------
export const authApi = {
  login: async (email, password) => (await axios.post('/auth/admin/login', { email, password })).data
};

// ---------------------------------------------------------------------------
// Standard CRUD modules — same getAll/get/add/modify/delete shape everywhere
// ---------------------------------------------------------------------------
export const governoratesApi = createResourceApi('/admin/governorates');
export const citiesApi = createResourceApi('/admin/cities');
export const districtsApi = createResourceApi('/admin/districts');
export const zonesApi = createResourceApi('/admin/zones');
export const rolesApi = createResourceApi('/admin/roles');
export const usersApi = createResourceApi('/admin/users');
usersApi.resetPassword = async (id, newPassword) =>
  (await axios.put(`/admin/users/reset-password/${id}`, { new_password: newPassword })).data.data;

// Captain/restaurant self-registration lands here (2026-08) instead of creating the account
// directly — verifying a request is what actually creates the captain/restaurant row
// (FinanceService-style atomic transaction, see backend/CLAUDE.md). `type` on each row is
// 'captain' or 'restaurant'; `payload` holds the type-specific fields (national_id/vehicle_type
// for captain, district_id/address for restaurant).
export const registerRequestsApi = createResourceApi('/admin/register-requests');
registerRequestsApi.pending = async () => (await axios.get('/admin/register-requests/pending')).data.data;
registerRequestsApi.verify = async (id) => (await axios.put(`/admin/register-requests/${id}/verify`)).data.data;
registerRequestsApi.reject = async (id, reason) => (await axios.put(`/admin/register-requests/${id}/reject`, { reason })).data.data;
export const captainStatusesApi = createResourceApi('/admin/captain-statuses');
export const bundlesApi = createResourceApi('/admin/bundles');
bundlesApi.active = async () => (await axios.get('/admin/bundles/active')).data.data;
// Restaurant-owned bundles (migration 076): fixed captains, and copying a bundle to a restaurant
// that has none.
bundlesApi.captains = async (id) => (await axios.get(`/admin/bundles/${id}/captains`)).data.data;
bundlesApi.addCaptain = async (id, captainId) =>
  (await axios.post(`/admin/bundles/${id}/captains`, { captain_id: captainId })).data.data;
bundlesApi.removeCaptain = async (id, captainId) => (await axios.delete(`/admin/bundles/${id}/captains/${captainId}`)).data.data;
bundlesApi.copy = async (id, { restaurantId, name }) =>
  (await axios.post(`/admin/bundles/${id}/copy`, { restaurant_id: restaurantId, name: name || undefined })).data.data;
export const feesApi = createResourceApi('/admin/fees');
export const restaurantPhonesApi = createResourceApi('/admin/restaurant-phones');
restaurantPhonesApi.byRestaurant = async (restaurantId) =>
  (await axios.get(`/admin/restaurant-phones/byRestaurant/${restaurantId}`)).data.data;
// Login password of one additional number (migration 066) — lets it log in to the restaurant app.
restaurantPhonesApi.setPassword = async (id, newPassword) =>
  (await axios.put(`/admin/restaurant-phones/${id}/password`, { new_password: newPassword })).data.data;
restaurantPhonesApi.clearPassword = async (id) => (await axios.delete(`/admin/restaurant-phones/${id}/password`)).data.data;
export const orderStatusesApi = createResourceApi('/admin/order-statuses');

// district_distance_matrix has no /add or /modify/:id on the backend — it's a single
// upsert endpoint (POST /set, keyed on the from/to pair). Defined fully here rather than
// via createResourceApi so nothing silently hits a 404.
// Zone travel-time matrix (migration 074) — same upsert-only shape as districtMatrixApi. payload:
// { from_zone_id, to_zone_id, distance_km, duration_minutes, is_estimated?, two_way? }.
export const zoneMatrixApi = {
  list: async () => (await axios.get('/admin/zone-matrix/getAll')).data.data,
  upsert: async (payload) => (await axios.post('/admin/zone-matrix/set', payload)).data.data,
  remove: async (id) => (await axios.delete(`/admin/zone-matrix/delete/${id}`)).data.data,
  // Shortest chain of zones { path: [{id,name}], minutes, km, segments } or null when not connected.
  route: async (from, to) => (await axios.get('/admin/zone-matrix/route', { params: { from, to } })).data.data
};

export const districtMatrixApi = {
  list: async () => (await axios.get('/admin/district-matrix/getAll')).data.data,
  getOne: async (id) => (await axios.get(`/admin/district-matrix/get/${id}`)).data.data,
  // payload may include `two_way: true` to also create/update the reverse (to -> from) row in
  // the same call — the backend resolves both directions via ORS independently and writes them
  // as one atomic unit, rather than the caller submitting /set twice.
  upsert: async (payload) => (await axios.post('/admin/district-matrix/set', payload)).data.data,
  remove: async (id) => (await axios.delete(`/admin/district-matrix/delete/${id}`)).data.data,
  lookup: async (from, to) => (await axios.get('/admin/district-matrix/lookup', { params: { from, to } })).data.data,
  // The graph route (Dijkstra over the matrix) between two districts — { path: [{id,name}, ...],
  // minutes } or null if they're not connected yet. Distinct from `lookup` above, which only
  // resolves a single number for one row, not the actual multi-hop path.
  route: async (from, to) => (await axios.get('/admin/district-matrix/route', { params: { from, to } })).data.data,
  // Replaces the direct connection between from_district_id/to_district_id with two segments
  // routed through via_district_id (plus the reverse of each when two_way, default true) — see
  // pages/location/InsertBetweenDialog.jsx. Returns { deleted: [...], created: [...] }.
  insertBetween: async (payload) => (await axios.post('/admin/district-matrix/insert-between', payload)).data.data
};

// district_polygons — same no-add/no-modify shape as districtMatrixApi above, its own independent
// resource (a district's boundary, zero or one polygon), not related to the matrix. GeoJSON in and
// out; coordinates are [lng, lat], the GeoJSON convention — see components/map/DistrictPolygonField.jsx.
export const districtPolygonsApi = {
  getByDistrict: async (districtId) => (await axios.get(`/admin/district-polygons/byDistrict/${districtId}`)).data.data,
  // Every polygon of a governorate's active districts in one call — [{ district_id, district_name,
  // city_id, zone_id, geojson }]. The District Polygons page filters these by city/zone itself.
  byGovernorate: async (governorateId) => (await axios.get(`/admin/district-polygons/byGovernorate/${governorateId}`)).data.data,
  upsert: async (districtId, geojson) => (await axios.post('/admin/district-polygons/set', { district_id: districtId, geojson })).data.data,
  remove: async (districtId) => (await axios.delete(`/admin/district-polygons/byDistrict/${districtId}`)).data.data,
  // Admin diagnostic tool (pages/location/DistrictPolygons.jsx's click-to-locate, and Districts.jsx's
  // point-search panel) — which district's drawn boundary (if any) contains this point. Returns an
  // array (usually 0 or 1 entries; more than one means two
  // boundaries overlap, worth surfacing rather than silently picking one).
  locate: async (lat, lon) => (await axios.get('/admin/district-polygons/locate', { params: { lat, lon } })).data.data
};

// Extra, non-standard endpoints layered on top of the standard shapes above
citiesApi.byGovernorate = async (governorateId) => (await axios.get(`/admin/cities/byGovernorate/${governorateId}`)).data.data;
districtsApi.byCity = async (cityId) => (await axios.get(`/admin/districts/byCity/${cityId}`)).data.data;
zonesApi.districts = async (zoneId) => (await axios.get(`/admin/zones/${zoneId}/districts`)).data.data;
zonesApi.assignDistrict = async (zoneId, districtId) =>
  (await axios.put(`/admin/zones/${zoneId}/assign-district`, { district_id: districtId })).data.data;
zonesApi.setSequence = async (zoneId, districtId, sequenceNumber) =>
  (await axios.put(`/admin/zones/${zoneId}/set-sequence`, { district_id: districtId, sequence_number: sequenceNumber })).data.data;
zonesApi.distance = async (from, to) => (await axios.get('/admin/zones/distance', { params: { from, to } })).data.data;
feesApi.byRestaurant = async (restaurantId) => (await axios.get(`/admin/fees/byRestaurant/${restaurantId}`)).data.data;
// Every active district in the restaurant's city with that restaurant's fee (amount null = no fee
// configured) and restriction_reason (non-null = an active delivery restriction blocks it).
feesApi.districtsFee = async (restaurantId) => (await axios.get(`/admin/fees/districts-fee/${restaurantId}`)).data.data;
// Excel upload: fees = [{ district_id, amount }]. Upserts all rows in one go (all-or-nothing);
// districts not listed are untouched. Returns { inserted, updated }. A 400 rejects with
// { error, details: [{ index, district_id?, error }] } — index is the position in `fees`.
feesApi.bulkUpsert = async (restaurantId, fees) => (await axios.post(`/admin/fees/bulk/${restaurantId}`, { fees })).data.data;

// Delivery restrictions (backend migrations 061-063) — temporarily stop chosen restaurants / every
// restaurant in chosen districts or zones from delivering TO a destination district. Every target
// entry has its own reason, sent to the restaurants it covers. One active restriction per
// district. Not a createResourceApi: no modify/delete, a restriction is edited by adding/removing
// targets and ended by lifting it.
export const deliveryRestrictionsApi = {
  // params: { active: true|false } — omitted returns both
  list: async (params) => (await axios.get('/admin/delivery-restrictions/getAll', { params })).data.data,
  getOne: async (id) => (await axios.get(`/admin/delivery-restrictions/get/${id}`)).data.data,
  // { district_id, targets: [{ restaurant_id | district_id | zone_id, reason }] } — 409 + existing_id if already restricted
  create: async (payload) => (await axios.post('/admin/delivery-restrictions/add', payload)).data.data,
  // { restaurant_id | district_id | zone_id (exactly one), reason }
  addTarget: async (id, target) => (await axios.post(`/admin/delivery-restrictions/${id}/targets`, target)).data.data,
  removeTarget: async (id, targetId) => (await axios.delete(`/admin/delivery-restrictions/${id}/targets/${targetId}`)).data.data,
  lift: async (id) => (await axios.put(`/admin/delivery-restrictions/${id}/lift`)).data.data
};

export const travelTimeApi = {
  lookup: async (from, to) => (await axios.get('/admin/travel-time/lookup', { params: { from, to } })).data.data,
  // Real point-to-point road route (ORS Directions) for arbitrary GPS coordinates — distinct
  // from `lookup` above, which only resolves district-ID pairs. Returns { distanceKm,
  // durationMinutes, path } — path is already [lat, lon] pairs, ready for a Leaflet Polyline.
  directions: async (fromLat, fromLon, toLat, toLon) =>
    (
      await axios.get('/admin/travel-time/directions', {
        params: { from_lat: fromLat, from_lon: fromLon, to_lat: toLat, to_lon: toLon }
      })
    ).data.data
};

// ---------------------------------------------------------------------------
// Captains — CRUD + verify/disable/enable + location history
// ---------------------------------------------------------------------------
export const captainsApi = createResourceApi('/admin/captains');
captainsApi.verify = async (id, isVerified) => (await axios.put(`/admin/captains/verify/${id}`, { is_verified: isVerified })).data.data;
captainsApi.disable = async (id) => (await axios.put(`/admin/captains/disable/${id}`)).data.data;
captainsApi.resetPassword = async (id, newPassword) =>
  (await axios.put(`/admin/captains/reset-password/${id}`, { new_password: newPassword })).data.data;
captainsApi.enable = async (id) => (await axios.put(`/admin/captains/enable/${id}`)).data.data;
captainsApi.locations = async (id, limit = 50) =>
  (await axios.get(`/admin/captain-locations/byCaptain/${id}`, { params: { limit } })).data.data;
// Uploads a photo file and returns its public URL — doesn't touch any captain row itself (see
// backend CaptainRouter.js's /upload-photo comment). The caller saves the returned url into
// `profile_photo` via the normal create/update call, same as pasting a URL by hand.
captainsApi.uploadPhoto = async (file) => {
  const form = new FormData();
  form.append('photo', file);
  return (await axios.post('/admin/captains/upload-photo', form)).data.data.url;
};

// ---------------------------------------------------------------------------
// Restaurants — CRUD + status + bundle subscription review
// ---------------------------------------------------------------------------
export const restaurantsApi = createResourceApi('/admin/restaurants');
restaurantsApi.setStatus = async (id, status) => (await axios.put(`/admin/restaurants/status/${id}`, { status })).data.data;
restaurantsApi.resetPassword = async (id, newPassword) =>
  (await axios.put(`/admin/restaurants/reset-password/${id}`, { new_password: newPassword })).data.data;
// Uploads a logo file and returns its public URL — same entity-agnostic pattern as
// captainsApi.uploadPhoto above.
restaurantsApi.uploadLogo = async (file) => {
  const form = new FormData();
  form.append('logo', file);
  return (await axios.post('/admin/restaurants/upload-logo', form)).data.data.url;
};
// No setBrand call here, deliberately — a restaurant is linked to a brand exactly one way (that
// brand's own POST /api/brand/branch-requests, approved by an admin), never a direct admin action
// (see RestaurantModel.js's own comment on why).

// Per-restaurant captain blacklist (migration 041) — admin-managed, future-offers-only (never
// revokes an order a blacklisted captain already has in hand).
export const restaurantCaptainBlacklistApi = {
  byRestaurant: async (restaurantId) => (await axios.get(`/admin/restaurant-captain-blacklist/byRestaurant/${restaurantId}`)).data.data,
  add: async (restaurantId, captainId, reason) =>
    (await axios.post('/admin/restaurant-captain-blacklist/add', { restaurant_id: restaurantId, captain_id: captainId, reason })).data.data,
  remove: async (restaurantId, captainId) => (await axios.delete(`/admin/restaurant-captain-blacklist/${restaurantId}/${captainId}`)).data
};

// Restaurant brands (migration 055) — a login one level above individual restaurant branches.
// No self-registration path exists for a brand (an admin always creates the row), unlike
// restaurants/captains.
export const restaurantBrandsApi = createResourceApi('/admin/restaurant-brands');
restaurantBrandsApi.resetPassword = async (id, newPassword) =>
  (await axios.put(`/admin/restaurant-brands/reset-password/${id}`, { new_password: newPassword })).data.data;
restaurantBrandsApi.branches = async (id) => (await axios.get(`/admin/restaurant-brands/${id}/branches`)).data.data;
// Attach an existing restaurant to the brand (admin-only, attach only — no detach, 2026-10).
restaurantBrandsApi.attachBranch = async (id, restaurantId) =>
  (await axios.put(`/admin/restaurant-brands/${id}/branches/${restaurantId}`)).data.data;

export const restaurantSubscriptionsApi = createResourceApi('/admin/restaurant-subscriptions');
restaurantSubscriptionsApi.bundleUsage = async () => (await axios.get('/admin/restaurant-subscriptions/bundle-usage')).data.data;
// Migration 084: { restaurantId, startsAt? }. Due now, it charges the wallet here (402 when short,
// nothing created); a future start queues it and charges on that date.
restaurantSubscriptionsApi.create = async ({ restaurantId, startsAt }) =>
  (await axios.post('/admin/restaurant-subscriptions/add', { restaurant_id: restaurantId, starts_at: startsAt })).data.data;
restaurantSubscriptionsApi.cancel = async (id) => (await axios.put(`/admin/restaurant-subscriptions/${id}/cancel`)).data.data;
// Migration 085: ends the current subscription now, no refund, and cancels a queued one.
restaurantSubscriptionsApi.end = async (id) => (await axios.put(`/admin/restaurant-subscriptions/${id}/end`)).data.data;
// Migration 088: charges what it owes, from its expiry date on. 402 if the wallet can't pay.
restaurantSubscriptionsApi.charge = async (id) => (await axios.put(`/admin/restaurant-subscriptions/${id}/charge`)).data.data;
restaurantSubscriptionsApi.byRestaurant = async (restaurantId) =>
  (await axios.get(`/admin/restaurant-subscriptions/byRestaurant/${restaurantId}`)).data.data;
// Extend the running subscription (migrations 076, 086): a later end date and/or extra captains
// (absolute value) on top of the bundle's max_captains. Either may be omitted.
restaurantSubscriptionsApi.extend = async (id, { extendedUntil, extraCaptains }) =>
  (await axios.put(`/admin/restaurant-subscriptions/${id}/extend`, { extended_until: extendedUntil, extra_captains: extraCaptains })).data.data;
// Pause the current subscription until a date/time (migration 078): the restaurant can't create
// orders meanwhile; the expiry and fixed captains don't change. Resume ends the pause early.
// getOne (GET /get/:id) returns the subscription with is_paused/paused_until and its `pauses` history.
restaurantSubscriptionsApi.pause = async (id, { pausedUntil, reason }) =>
  (await axios.put(`/admin/restaurant-subscriptions/${id}/pause`, { paused_until: pausedUntil, reason })).data.data;
restaurantSubscriptionsApi.resume = async (id) => (await axios.put(`/admin/restaurant-subscriptions/${id}/resume`)).data.data;

// ---------------------------------------------------------------------------
// Orders, trips, dispatch
// ---------------------------------------------------------------------------
export const ordersApi = createResourceApi('/admin/orders');
ordersApi.list = async (params) => (await axios.get('/admin/orders/getAll', { params })).data.data; // override: supports limit/offset
// Admin "New Order" — ordersApi.create (POST /admin/orders/add, from the factory) takes
// { restaurant_id, district_id, delivery_fee?, captain_id?, receipt_image?, avg_preparation_minutes? }.
// uploadReceipt uploads the image file first and returns its URL for receipt_image — same
// pattern as restaurantsApi.uploadLogo. With captain_id the order is offered to that captain first (real
// offer, falls back to auto-dispatch on timeout); without it, it auto-dispatches.
// eligibleCaptainsForCreate feeds the captain picker: ready + in-progress captains near the
// restaurant, each with _distanceKm, can_afford (wallet covers this order's request fee), and —
// for a captain on a trip, once a district is given — can_stack/stack_blocked_reason (the same
// stacking checks the automatic dispatch applies).
ordersApi.uploadReceipt = async (file) => {
  const form = new FormData();
  form.append('receipt_image', file);
  return (await axios.post('/admin/orders/upload-receipt', form)).data.data.url;
};
ordersApi.eligibleCaptainsForCreate = async ({ restaurantId, deliveryFee, districtId, avgPreparationMinutes }) =>
  (
    await axios.get('/admin/orders/eligible-captains', {
      params: {
        restaurant_id: restaurantId,
        delivery_fee: deliveryFee,
        district_id: districtId,
        avg_preparation_minutes: avgPreparationMinutes
      }
    })
  ).data.data;
ordersApi.byRestaurant = async (restaurantId) => (await axios.get(`/admin/orders/byRestaurant/${restaurantId}`)).data.data;
ordersApi.byCaptain = async (captainId) => (await axios.get(`/admin/orders/byCaptain/${captainId}`)).data.data;
ordersApi.byTrip = async (tripId) => (await axios.get(`/admin/orders/byTrip/${tripId}`)).data.data;
// Live Tracking: orders not yet accepted (created / assigned / hold), oldest first, with names.
ordersApi.awaitingCaptain = async () => (await axios.get('/admin/orders/awaiting-captain')).data.data;
ordersApi.late = async () => (await axios.get('/admin/orders/late')).data.data;
// Orders search — order id / trip id / captain id / restaurant id / creation date, any/all
// optional. params keys match the query string the backend expects exactly.
ordersApi.search = async (params) => (await axios.get('/admin/orders/search', { params })).data.data;
// ORD-REASSIGN-01 — a recovery action available only on an already-cancelled order. Which path
// applies (redispatch via hold, vs. a specific-captain picker) is fully determined server-side by
// the order's own cancelled_from_status, never an admin choice — captainId is only required for
// the picked_up/delivered path; the hold path ignores it. eligibleCaptains always returns an
// array (empty for the hold path, since there's no captain picker for that one).
// Admin override of the captain-side destination arrival check — picked-up orders only.
ordersApi.markDelivered = async (id, reason) => (await axios.put(`/admin/orders/${id}/mark-delivered`, { reason: reason || null })).data.data;
ordersApi.reassignEligibleCaptains =async (id) => (await axios.get(`/admin/orders/${id}/reassign/eligible-captains`)).data.data;
ordersApi.reassign = async (id, captainId) =>
  (await axios.post(`/admin/orders/${id}/reassign`, captainId ? { captain_id: captainId } : {})).data.data;
// ORD-CANCEL-01 — compensation_source ('restaurant'|'platform'|'none') only matters at
// 'at_restaurant'; compensation_amount only matters at 'picked_up'/'delivered' and is
// server-clamped regardless of what's sent. Both optional — omit for every other status.
ordersApi.cancel = async (id, reason, compensationSource, compensationAmount) =>
  (
    await axios.put(`/admin/orders/cancel/${id}`, {
      reason,
      compensation_source: compensationSource,
      compensation_amount: compensationAmount
    })
  ).data.data;
// COD settlement (migration 045) — admin-side dispute-resolution equivalent of the restaurant
// app's own mark-paid. Freely toggleable either way (2026-09) regardless of cod_amount, which is
// purely informational now — isPaid defaults true (the common case: confirming payment).
ordersApi.markPaid = async (id, isPaid = true) => (await axios.put(`/admin/orders/${id}/mark-paid`, { is_paid: isPaid })).data.data;

export const tripsApi = createResourceApi('/admin/trips');
tripsApi.list = async () => (await axios.get('/admin/trips/getAll')).data.data;
tripsApi.byCaptain = async (captainId) => (await axios.get(`/admin/trips/byCaptain/${captainId}`)).data.data;
tripsApi.fee = async (id) => (await axios.get(`/admin/trips/${id}/fee`)).data.data;
// Trips search — id / order id / captain id / restaurant id / creation date, any/all optional.
tripsApi.search = async (params) => (await axios.get('/admin/trips/search', { params })).data.data;
// Busy-wait (migration 047) — ends this trip's wait immediately instead of waiting out
// wait_duration_minutes; { ended, reason? } — a no-op with a reason (not an error) if the
// captain's since accepted a new stacking order and isn't eligible to be flipped to busy anymore.
tripsApi.endWait = async (id) => (await axios.put(`/admin/trips/${id}/end-wait`)).data.data;

export const dispatchAttemptsApi = {
  list: async () => (await axios.get('/admin/dispatch-attempts/getAll')).data.data,
  byOrder: async (orderId) => (await axios.get(`/admin/dispatch-attempts/byOrder/${orderId}`)).data.data,
  responses: async (orderId) => (await axios.get(`/admin/dispatch-attempts/byOrder/${orderId}/responses`)).data.data,
  byCaptain: async (captainId) => (await axios.get(`/admin/dispatch-attempts/byCaptain/${captainId}`)).data.data
};

export const orderStatusLogsApi = {
  list: async (params) => (await axios.get('/admin/order-status-logs/getAll', { params })).data.data,
  byOrder: async (orderId) => (await axios.get(`/admin/order-status-logs/byOrder/${orderId}`)).data.data
};

// ---------------------------------------------------------------------------
// Finance
// ---------------------------------------------------------------------------
export const walletsApi = {
  list: async () => (await axios.get('/admin/wallets/getAll')).data.data,
  getOne: async (id) => (await axios.get(`/admin/wallets/get/${id}`)).data.data,
  byOwner: async (type, id) => (await axios.get(`/admin/wallets/byOwner/${type}/${id}`)).data.data,
  transactions: async (walletId, params) => (await axios.get(`/admin/wallets/${walletId}/transactions`, { params })).data.data,
  setActive: async (id, isActive) => (await axios.put(`/admin/wallets/setActive/${id}`, { is_active: isActive })).data.data,
  adjustment: async (payload) => (await axios.post('/admin/wallets/adjustment', payload)).data.data,
  topUp: async (payload) => (await axios.post('/admin/wallets/topUp', payload)).data.data,
  reconciliationCheck: async () => (await axios.get('/admin/wallets/reconciliation-check')).data.data
};

export const walletTransactionsApi = {
  list: async (params) => (await axios.get('/admin/wallet-transactions/getAll', { params })).data.data,
  getOne: async (id) => (await axios.get(`/admin/wallet-transactions/get/${id}`)).data.data
};

// Captain- or restaurant-submitted wallet top-up requests (2026-08: restaurants can request
// too, not just captains — see backend/CLAUDE.md's Finance section). Rows live directly in
// wallet_transactions (tag='top_up', status column) since migration 018 merged the former
// wallet_topup_requests table away — pending/approve/cancel shape mirrors
// restaurantSubscriptionsApi above. Approving credits the wallet server-side
// (FinanceService.approveTopUpRequest) and sends the owner a notification; cancelling sends one
// too. `owner_type`/`owner_id`/`owner_name`/`owner_phone` on each row identify who it belongs to
// — either a captain or a restaurant.
export const walletTopUpRequestsApi = createResourceApi('/admin/wallet-topup-requests');
walletTopUpRequestsApi.pending = async () => (await axios.get('/admin/wallet-topup-requests/pending')).data.data;
walletTopUpRequestsApi.byOwner = async (ownerType, ownerId) =>
  (await axios.get(`/admin/wallet-topup-requests/byOwner/${ownerType}/${ownerId}`)).data.data;
walletTopUpRequestsApi.approve = async (id) => (await axios.put(`/admin/wallet-topup-requests/${id}/approve`)).data.data;
walletTopUpRequestsApi.cancel = async (id, reason) => (await axios.put(`/admin/wallet-topup-requests/${id}/cancel`, { reason })).data.data;

// Singleton platform-wide config — app fee percentage charged from a captain's wallet on
// delivery, the max-orders-per-captain stacking cap, the max-captain-search-radius (km) both
// dispatch engines enforce, which stacking-feasibility algorithm the in-progress candidate
// search uses ('ors' or 'graph' — see backend migration 028), and which channel mirrors a live
// blind offer for real-time listening ('websocket' or 'firestore' — see backend migration 064).
export const platformSettingsApi = {
  get: async () => (await axios.get('/admin/platform-settings')).data.data,
  // ORD-CANCEL-01 (migration 036/037) added the compensation/cap/wallet-floor fields; migration
  // 038 adds topup_phone_numbers. That's 13 fields now, all the same primitive types — past the
  // point where positional args are safe (transposing two numbers is an easy, silent mistake) —
  // so this takes one settings object, keyed exactly like the PUT body, instead.
  update: async (settings) => (await axios.put('/admin/platform-settings', settings)).data.data
};

// ---------------------------------------------------------------------------
// Social — ratings, notifications
// ---------------------------------------------------------------------------
export const ratingsApi = {
  list: async (params) => (await axios.get('/admin/ratings/getAll', { params })).data.data,
  byRateable: async (type, id) => (await axios.get(`/admin/ratings/byRateable/${type}/${id}`)).data.data,
  byOrder: async (orderId) => (await axios.get(`/admin/ratings/byOrder/${orderId}`)).data.data
};

export const notificationsApi = {
  list: async (params) => (await axios.get('/admin/notifications/getAll', { params })).data.data,
  send: async (payload) => (await axios.post('/admin/notifications/add', payload)).data.data
};

// ---------------------------------------------------------------------------
// Content pages — admin-editable HTML pages keyed by slug (about-app,
// terms-and-conditions, ...), served publicly at GET /api/content/:slug for the mobile apps.
// ---------------------------------------------------------------------------
export const contentsApi = createResourceApi('/admin/contents');

// Android APKs listed as download buttons on the landing page (backend migration 065). Uploading
// is what publishes a release — the landing page reads every row via the public
// GET /api/app-releases. create() is multipart (app, label, file); onProgress gets 0-100.
// update() only changes the label.
export const appReleasesApi = {
  list: async () => (await axios.get('/admin/app-releases/getAll')).data.data,
  create: async ({ app, label, file }, onProgress) => {
    const form = new FormData();
    form.append('app', app);
    form.append('label', label);
    form.append('file', file);
    // Whole body: { data: release, pruned: <count of oldest releases removed by the 5-per-app cap> }
    return (
      await axios.post('/admin/app-releases/add', form, {
        onUploadProgress: (e) => e.total && onProgress?.(Math.round((e.loaded / e.total) * 100))
      })
    ).data;
  },
  update: async (id, label) => (await axios.put(`/admin/app-releases/modify/${id}`, { label })).data.data,
  remove: async (id) => (await axios.delete(`/admin/app-releases/delete/${id}`)).data.data,
  // Make this release the one the landing page offers for its app.
  setCurrent: async (id) => (await axios.put(`/admin/app-releases/${id}/current`)).data.data,
  // CI upload key: details of the active one (or null), generate (returns the full key once), revoke.
  getUploadKey: async () => (await axios.get('/admin/app-releases/upload-key')).data.data,
  generateUploadKey: async () => (await axios.post('/admin/app-releases/upload-key')).data.data,
  revokeUploadKey: async () => (await axios.delete('/admin/app-releases/upload-key')).data.data,
  // App versions (backend migration 083): each has its own appKey/appSecret. createVersion()
  // returns app_key and app_secret; the secret is never returned again.
  listVersions: async () => (await axios.get('/admin/app-releases/versions')).data.data,
  createVersion: async ({ app, platform, label }) => (await axios.post('/admin/app-releases/versions', { app, platform, label })).data.data,
  setForceUpdate: async (id, forceUpdate) =>
    (await axios.put(`/admin/app-releases/versions/${id}/force-update`, { force_update: forceUpdate })).data.data
};
